/**
 * Base Widget class
 *
 * This class provides common functionality that all widgets inherit.
 * It handles configuration, initialization, internationalization,
 * styling, and communication with the parent window.
 */

import { getTranslation } from "@shared/i18n/i18n";
import { debounce } from "@shared/utils/lazyLoader";

class Widget {
  /**
   * Constructor for the widget
   * @param {Object} options Configuration options for the widget
   */
  constructor(options = {}) {
    // Store widget identifier
    this.name = options.name || "widget";

    // Parse URL parameters
    this.params = new URLSearchParams(window.location.search);

    // Initialize configuration with defaults and URL params
    this.config = {
      mainColor: `#${this.params.get("main_color") || "3498db"}`,
      secondaryColor: `#${this.params.get("secondary_color") || "e0e0e0"}`,
      textColor: `#${this.params.get("text_color") || "334155"}`,
      borderRadius: `${this.params.get("border_radius") || "8"}px`,
      fontFamily: this.params.get("font_family") || "sans-serif",
      language: this.params.get("lang") || "en",
      token: this.params.get("token"),
      privacyPolicyUrl: this.params.get("privacy_policy_url"),
      displayValue: this.params.get("display_value") !== "false" // defaults to true
    };

    // State management
    this.state = {
      currentStep: 1,
      formData: {},
      isInitialized: false,
      ...options.state
    };

    // Get the iframe ID from the window name
    this.frameId = window.name || "";

    // Performance monitoring
    this.performanceMarks = {};
    this.startPerformanceMark("widget-init");

    // Debounced height reporting for performance
    this.debouncedHeightReport = debounce(() => {
      this.sendHeightToParent();
    }, 100);

    // Auto initialize if container element exists
    if (options.autoInit !== false) {
      this.init();
    }
  }

  /**
   * Initialize the widget
   */
  init() {
    if (this.state.isInitialized) return;

    // Apply CSS variables
    this.applyCssVariables();

    // Apply translations
    this.applyTranslations();

    // Add event listeners
    this.addEventListeners();

    // Register resize observer
    this.setupResizeObserver();

    // Set initialization flag
    this.state.isInitialized = true;

    // Performance mark
    this.endPerformanceMark("widget-init");

    // Notify parent that widget is ready
    this.sendMessageToParent({
      type: "ready",
      frameId: this.frameId,
      widgetName: this.name,
      performance: this.getPerformanceMetrics()
    });
  }

  /**
   * Apply CSS variables based on configuration
   */
  applyCssVariables() {
    const root = document.documentElement;

    root.style.setProperty("--primary-color", this.config.mainColor);
    root.style.setProperty("--primary-color-focus", `${this.config.mainColor}ee`);
    root.style.setProperty("--primary-color-border", `${this.config.mainColor}11`);
    root.style.setProperty("--background-color", `${this.config.mainColor}44`);
    root.style.setProperty("--secondary-color", this.config.secondaryColor);
    root.style.setProperty("--text-color", this.config.textColor);
    root.style.setProperty("--border-color", `${this.config.textColor}33`);
    root.style.setProperty("--border-radius", this.config.borderRadius);
    root.style.setProperty("--font-family", this.config.fontFamily);
  }

  /**
   * Apply translations to the widget
   */
  applyTranslations() {
    document.querySelectorAll("[data-i18n]").forEach((element) => {
      const key = element.getAttribute("data-i18n");
      const type = element.getAttribute("data-i18n-type") || "text";

      if (type === "text") {
        element.textContent = this.getText(key);
      } else if (type === "placeholder") {
        element.placeholder = this.getText(key);
      } else if (type === "title") {
        element.title = this.getText(key);
      } else if (type === "aria-label") {
        element.setAttribute("aria-label", this.getText(key));
      }
    });
  }

  /**
   * Get translated text for a key
   * @param {string} key The translation key
   * @returns {string} Translated text
   */
  getText(key) {
    return getTranslation(key, this.config.language);
  }

  /**
   * Add global event listeners
   */
  addEventListeners() {
    // Listen for messages from parent window
    window.addEventListener("message", this.handleParentMessage.bind(this));

    // Listen for load event to send initial height
    window.addEventListener("load", () => {
      setTimeout(() => this.sendHeightToParent(), 100);
    });

    // Handle visibility changes (e.g., tab focus)
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.sendHeightToParent();
      }
    });
  }

  /**
   * Handle messages from parent window
   * @param {MessageEvent} event Message event
   */
  handleParentMessage(event) {
    // Validate message is for this widget
    if (!event.data || event.data.frameId !== this.frameId) return;

    // Handle different message types
    switch (event.data.type) {
      case "updateConfig":
        this.updateConfig(event.data.config);
        break;
      case "getState":
        this.sendMessageToParent({
          type: "state",
          frameId: this.frameId,
          state: this.state
        });
        break;
      case "reset":
        this.reset();
        break;
      default:
        // Custom message handling for widget-specific functionality
        this.handleCustomMessage(event.data);
        break;
    }
  }

  /**
   * Handle custom messages - to be overridden by subclasses
   * @param {Object} _data Message data
   */
  handleCustomMessage(_data) {
    // To be implemented by widget subclasses
  }

  /**
   * Update widget configuration
   * @param {Object} newConfig New configuration options
   */
  updateConfig(newConfig) {
    if (!newConfig || typeof newConfig !== "object") return;

    // Update config
    this.config = {
      ...this.config,
      ...newConfig
    };

    // Apply updated configuration
    this.applyCssVariables();
    this.applyTranslations();

    // Notify config was updated
    this.sendMessageToParent({
      type: "configUpdated",
      frameId: this.frameId
    });
  }

  /**
   * Set up resize observer to track content height changes
   */
  setupResizeObserver() {
    if (typeof ResizeObserver !== "undefined") {
      // Modern browsers - use ResizeObserver
      this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
      this.resizeObserver.observe(document.body);
    } else {
      // Fallback for older browsers - check height periodically
      this.resizeInterval = setInterval(() => {
        this.sendHeightToParent();
      }, 1000);
    }
  }

  /**
   * Handle resize events from ResizeObserver (debounced for performance)
   */
  handleResize() {
    this.debouncedHeightReport();
  }

  /**
   * Send current height to parent window
   */
  sendHeightToParent() {
    const height = document.body.scrollHeight;
    this.sendMessageToParent({
      type: "resize",
      frameId: this.frameId,
      height
    });
  }

  /**
   * Send a message to the parent window
   * @param {Object} message Message to send
   */
  sendMessageToParent(message) {
    if (window.parent) {
      window.parent.postMessage(message, "*");
    }
  }

  /**
   * Update widget state
   * @param {Object} newState State updates
   */
  setState(newState) {
    this.state = {
      ...this.state,
      ...newState
    };

    // Optional: Trigger an update event that components can listen for
    const event = new CustomEvent("widget-state-update", { detail: this.state });
    document.dispatchEvent(event);
  }

  /**
   * Reset the widget to initial state
   */
  reset() {
    // Reset state to defaults
    this.state = {
      ...this.state,
      currentStep: 1,
      formData: {}
    };

    // Widget-specific reset implementation
    this.performReset();

    // Notify reset completed
    this.sendMessageToParent({
      type: "resetComplete",
      frameId: this.frameId
    });
  }

  /**
   * Perform widget-specific reset operations
   * To be implemented by subclasses
   */
  performReset() {
    // To be implemented by widget subclasses
  }

  /**
   * Start performance measurement
   * @param {string} name Mark name
   */
  startPerformanceMark(name) {
    this.performanceMarks[name] = performance.now();
  }

  /**
   * End performance measurement
   * @param {string} name Mark name
   */
  endPerformanceMark(name) {
    if (this.performanceMarks[name]) {
      const duration = performance.now() - this.performanceMarks[name];
      this.performanceMarks[`${name}-duration`] = duration;
    }
  }

  /**
   * Get performance metrics
   * @returns {Object} Performance metrics
   */
  getPerformanceMetrics() {
    return {
      initTime: this.performanceMarks["widget-init-duration"] || 0,
      timestamp: Date.now()
    };
  }

  /**
   * Clean up widget resources
   */
  destroy() {
    // Remove resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Clear resize interval if it exists
    if (this.resizeInterval) {
      clearInterval(this.resizeInterval);
    }

    // Remove event listeners
    window.removeEventListener("message", this.handleParentMessage);

    // Set initialized flag to false
    this.state.isInitialized = false;
  }
}

export default Widget;
